import React, { useState } from "react";
import { Menu, X, Headphones, Shield, LogOut } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/audioflip/LanguageSwitcher";
import { useAuth } from "@/auth/AuthContext";
import LoginModal from "@/auth/LoginModal";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const { t } = useLanguage();
  const { user, isAdmin, logout } = useAuth();

  const links = [
    { href: "#inicio", label: t("nav.home"), id: "home" },
    { href: "#caracteristicas", label: t("nav.features"), id: "features" },
    { href: "#precios", label: t("nav.pricing"), id: "pricing" },
    ...(isAdmin ? [{ href: "#admin", label: "Admin", id: "admin" }] : []),
  ];

  return (
    <>
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 border-b border-zinc-200/60 bg-white/70 backdrop-blur-xl backdrop-saturate-150"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a
          href="#inicio"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-md bg-[#002FA7] text-white transition-transform duration-200 group-hover:scale-105">
            <Headphones className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="font-serif text-2xl font-bold tracking-tight text-zinc-950">
            AudioFlip
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600 hover:text-[#002FA7] transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <>
              {isAdmin && (
                <span
                  data-testid="admin-badge"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#002FA7] px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white font-bold"
                >
                  <Shield className="h-3 w-3" /> Admin
                </span>
              )}
              <button
                data-testid="navbar-logout-btn"
                onClick={logout}
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 font-sans text-sm font-medium text-zinc-900 hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-200"
              >
                <LogOut className="h-3.5 w-3.5" />
                {user.email.split("@")[0]}
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="navbar-login-btn"
                onClick={() => setLoginOpen(true)}
                className="rounded-md border border-zinc-200 bg-white px-4 py-2 font-sans text-sm font-medium text-zinc-900 hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-200"
              >
                {t("nav.login")}
              </button>
              <button
                data-testid="navbar-signup-btn"
                onClick={() => setLoginOpen(true)}
                className="rounded-md bg-[#002FA7] px-4 py-2 font-sans text-sm font-semibold text-white hover:bg-[#00258A] transition-colors duration-200"
              >
                {t("nav.signup")}
              </button>
            </>
          )}
        </div>

        <div className="flex md:hidden items-center gap-2">
          <LanguageSwitcher />
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-zinc-200 text-zinc-800"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden border-t border-zinc-200 bg-white px-6 py-4 space-y-3"
        >
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-600 py-2"
            >
              {l.label}
            </a>
          ))}
          <div className="flex gap-2 pt-2">
            {user ? (
              <button
                onClick={logout}
                className="flex-1 rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium"
              >
                Log out ({user.email.split("@")[0]})
              </button>
            ) : (
              <button
                onClick={() => setLoginOpen(true)}
                className="flex-1 rounded-md bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white"
              >
                {t("nav.login")}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
    <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  );
}
